import type {
  ContentfulHeadlineBlock,
  ContentfulLinkAction,
} from "@scott/contentful/types";
import { logger } from "@scott/logger";
import type {
  HeroSectionSlide,
  HeroSectionSliderMedia,
} from "@/scott/ui/blocks/hero-section/hero-section-slider";
import { adaptButtons } from "@/services/contentful/assemblies/page/adapter/button-adapter";
import {
  CLOUDINARY_TRANSFORMATION_D2C_TALL,
  CLOUDINARY_TRANSFORMATION_D2C_ULTRAWIDE,
  CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN,
  extractCloudinaryAssetUrl,
  extractMediaCreativeName,
  getBlockId,
  getCloudinarySrcSet,
  resolveTransform,
} from "@/services/contentful/assemblies/page/adapter/utils";

import type {
  HeroSectionSliderPageBlock,
  SectionHeroPageBlock,
} from "@/types/page-block";

// biome-ignore lint/style/noMagicNumbers: srcset candidate widths
const HERO_SRCSET_WIDTHS = [640, 1024, 1536, 1920, 2560];
const HERO_DEFAULT_WIDTH = 1536;
const DEFAULT_AUTOPLAY_DURATION_SECONDS = 5;
const MIN_AUTOPLAY_DURATION_SECONDS = 2;
const MAX_AUTOPLAY_DURATION_SECONDS = 30;
const MILLISECONDS_PER_SECOND = 1000;
const STATIC_HERO_MAX_MEDIA = 2;

// Base transforms per layout — resolveTransform is applied per image if hasFocalPoint is set.
const HERO_TRANSFORMS = {
  viewport: {
    mobile: CLOUDINARY_TRANSFORMATION_D2C_TALL,
    desktop: CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN,
  },
  "grid-16-9": {
    mobile: CLOUDINARY_TRANSFORMATION_D2C_TALL,
    desktop: CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN,
  },
  "grid-21-9": {
    mobile: CLOUDINARY_TRANSFORMATION_D2C_TALL,
    desktop: CLOUDINARY_TRANSFORMATION_D2C_ULTRAWIDE,
  },
} as const;

function heroSrcSet(src: string, transform: string | string[]) {
  return getCloudinarySrcSet(src, {
    transform,
    widths: HERO_SRCSET_WIDTHS,
    defaultWidth: HERO_DEFAULT_WIDTH,
  });
}

type SectionHeroMediaImage = {
  __typename: "MediaImage";
  sys?: { id: string };
  alternativeText?: string | null;
  hasFocalPoint?: boolean | null;
  image?: unknown;
};

type SectionHeroMediaVideo = {
  __typename: "MediaVideo";
  sys?: { id: string };
  externalVideoUrl?: string | null;
  poster?: unknown;
  video?: unknown;
};

type SectionHeroMediaItem = SectionHeroMediaImage | SectionHeroMediaVideo;

function isCloudinarySvg(data: unknown): boolean {
  return (
    Array.isArray(data) &&
    typeof data[0] === "object" &&
    data[0] !== null &&
    "format" in data[0] &&
    data[0].format === "svg"
  );
}

/** The fields a Hero contributes when it is rendered as one slider slide. */
export interface SectionHeroSlideInput {
  sys?: { id: string };
  __typename: "SectionHero";
  internalName: string;
  content?: ContentfulHeadlineBlock;
  svg?: SectionHeroMediaImage | null;
  textColor:
    | "black"
    | "white"
    | "alabastergrey"
    | "blazeorange"
    | "neonchartreuse";
  backgroundColor: "white" | "whitesmoke" | "black";
  headlineSize?: "small" | "medium" | "large" | null;
  textHorizontalPosition: "left" | "center";
  textVerticalPosition: "top" | "center" | "bottom";
  buttonsPosition: "left" | "center" | "right";
  mediaCollection?: {
    items?: Array<SectionHeroMediaItem | null> | null;
  } | null;
  ctaCollection?: { items?: Array<ContentfulLinkAction | null> | null } | null;
}

export interface SectionHeroInput extends SectionHeroSlideInput {
  heroLayout: "full" | "wide" | "standard";
  presentation?: "single" | "slider" | null;
  autoplay?: boolean | null;
  autoplayDuration?: number | null;
  additionalSlidesCollection?: {
    items?: Array<SectionHeroSlideInput | null> | null;
  } | null;
}

const layoutMap = {
  full: "viewport",
  wide: "grid-21-9",
  standard: "grid-16-9",
} as const;

type HeroImage = {
  sys?: { id: string };
  src: string;
  srcSet?: string;
  alt: string;
  mobileSrc?: string;
  mobileSrcSet?: string;
  type: "Image";
};

type HeroVideo = {
  sys?: { id: string };
  src: string;
  poster?: string;
  type: "Video";
};

type MediaResult = HeroImage[] | HeroVideo;

function extractImage(
  mediaItem: SectionHeroMediaImage,
  contentSize: keyof typeof HERO_TRANSFORMS
): HeroImage | undefined {
  const transforms = HERO_TRANSFORMS[contentSize];
  const src = extractCloudinaryAssetUrl(mediaItem.image);

  if (!src) {
    return;
  }

  const desktop = heroSrcSet(
    src,
    resolveTransform(transforms.desktop, Boolean(mediaItem.hasFocalPoint))
  );
  const mobile = heroSrcSet(
    src,
    resolveTransform(transforms.mobile, Boolean(mediaItem.hasFocalPoint))
  );

  return {
    sys: mediaItem.sys,
    src: desktop?.src ?? src,
    srcSet: desktop?.srcSet,
    mobileSrc: mobile?.src,
    mobileSrcSet: mobile?.srcSet,
    alt: mediaItem.alternativeText ?? "",
    type: "Image",
  };
}

function extractVideo(
  mediaItem?: SectionHeroMediaVideo
): HeroVideo | undefined {
  const src =
    extractCloudinaryAssetUrl(mediaItem?.video) ||
    mediaItem?.externalVideoUrl ||
    "";

  if (!src) {
    return;
  }

  return {
    src,
    poster: extractCloudinaryAssetUrl(mediaItem?.poster) || undefined,
    type: "Video",
  };
}

function extractImagesOrVideo(
  mediaCollection: SectionHeroInput["mediaCollection"],
  contentSize: keyof typeof HERO_TRANSFORMS
): MediaResult {
  const items = mediaCollection?.items
    ?.slice(0, STATIC_HERO_MAX_MEDIA)
    .filter((item): item is SectionHeroMediaItem => item !== null);

  if (!items) {
    return [];
  }

  const images = items
    .filter(
      (item): item is SectionHeroMediaImage => item.__typename === "MediaImage"
    )
    .map((mediaItem) => extractImage(mediaItem, contentSize))
    .filter((image): image is HeroImage => Boolean(image));

  if (images.length > 0) {
    return images;
  }

  const videoItem = items.find(
    (item): item is SectionHeroMediaVideo => item.__typename === "MediaVideo"
  );
  return extractVideo(videoItem) ?? [];
}

type HeroSlideContent = Omit<HeroSectionSlide, "id" | "media">;

/** Headline or SVG, buttons, colours and placement of a Hero entry. */
function adaptHeroContent(
  item: SectionHeroSlideInput,
  basePath?: string
): HeroSlideContent {
  const svgImage = item.svg?.image;
  const svgSrc = extractCloudinaryAssetUrl(svgImage);
  const svg =
    isCloudinarySvg(svgImage) && svgSrc
      ? {
          src: svgSrc,
          alt: item.svg?.alternativeText ?? undefined,
          sys: item.svg?.sys,
        }
      : undefined;

  return {
    promotion: {
      name: item.internalName,
      creativeName: extractMediaCreativeName(item.mediaCollection?.items),
    },
    title: svg ? undefined : item.content,
    svg,
    buttons: adaptButtons(item.ctaCollection?.items, basePath),
    color: item.textColor,
    backgroundColor: item.backgroundColor,
    headlineSize: item.headlineSize ?? "medium",
    textHorizontalPosition: item.textHorizontalPosition,
    textVerticalPosition: item.textVerticalPosition,
    buttonsPosition: item.buttonsPosition,
  };
}

function adaptSingleHero(
  item: SectionHeroInput,
  index: number,
  basePath?: string
): SectionHeroPageBlock {
  const contentSize = layoutMap[item.heroLayout];

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: item.__typename,
    internalName: item.internalName,
    ...adaptHeroContent(item, basePath),
    media: extractImagesOrVideo(item.mediaCollection, contentSize),
    contentSize,
  };
}

function normalizeAutoplayDelay(duration?: number | null): number {
  const roundedDuration = Math.round(
    duration ?? DEFAULT_AUTOPLAY_DURATION_SECONDS
  );
  const durationSeconds = Math.min(
    MAX_AUTOPLAY_DURATION_SECONDS,
    Math.max(MIN_AUTOPLAY_DURATION_SECONDS, roundedDuration)
  );

  return durationSeconds * MILLISECONDS_PER_SECOND;
}

function extractSliderMedia(
  mediaItem: SectionHeroMediaItem
): HeroSectionSliderMedia | undefined {
  if (mediaItem.__typename === "MediaImage") {
    const image = extractImage(mediaItem, "viewport");
    return image ? [image] : undefined;
  }

  const video = extractVideo(mediaItem);
  if (!(video?.poster && video.src)) {
    return;
  }

  return {
    ...video,
    poster: video.poster,
  };
}

/**
 * A slide before its ID is allocated. `media` is undefined when the Hero has
 * no image or poster-backed video a slider can show.
 */
type SlideCandidate = {
  sourceId?: string;
  content: HeroSlideContent;
  media?: HeroSectionSliderMedia;
};

/** A Hero becomes one slide: its own content over its first usable medium. */
function toSlideCandidate(
  hero: SectionHeroSlideInput,
  basePath?: string
): SlideCandidate {
  const media = (hero.mediaCollection?.items ?? [])
    .map((mediaItem) => (mediaItem ? extractSliderMedia(mediaItem) : undefined))
    .find(Boolean);

  return {
    sourceId: hero.sys?.id,
    content: adaptHeroContent(hero, basePath),
    media,
  };
}

function allocateSlideId(
  contentfulId: string | undefined,
  slideIndex: number,
  rootBlockId: string,
  usedIds: Set<string>,
  reservedContentfulIds: ReadonlySet<string>
): string {
  if (contentfulId && !usedIds.has(contentfulId)) {
    usedIds.add(contentfulId);
    return contentfulId;
  }

  const fallbackBase = `${rootBlockId}-slide-${slideIndex}`;
  let fallbackId = fallbackBase;
  let disambiguator = 1;

  while (usedIds.has(fallbackId) || reservedContentfulIds.has(fallbackId)) {
    fallbackId = `${fallbackBase}-${disambiguator}`;
    disambiguator += 1;
  }

  usedIds.add(fallbackId);
  return fallbackId;
}

export function SectionHeroAdapter(
  item: SectionHeroInput,
  index: number,
  basePath?: string
): SectionHeroPageBlock | HeroSectionSliderPageBlock {
  const staticRootHero = adaptSingleHero(item, index, basePath);

  if (item.presentation !== "slider") {
    return staticRootHero;
  }

  const rootBlockId = getBlockId(item, index);
  const usedSlideIds = new Set<string>();
  // The Hero itself is the first slide, followed by its Additional slides.
  const candidates = [
    item,
    ...(item.additionalSlidesCollection?.items ?? []),
  ].map((hero) => (hero ? toSlideCandidate(hero, basePath) : null));
  const reservedContentfulIds = new Set(
    candidates.flatMap((candidate) =>
      candidate?.sourceId ? [candidate.sourceId] : []
    )
  );
  const slides = candidates.flatMap(
    (candidate, slideIndex): HeroSectionSlide[] => {
      if (!candidate) {
        return [];
      }

      const { sourceId, content, media } = candidate;
      if (!media) {
        logger.warn(
          "SectionHeroAdapter: skipping a slide with a missing media source or video poster",
          { sourceId, blockId: rootBlockId }
        );
        return [];
      }

      const id = allocateSlideId(
        sourceId,
        slideIndex,
        rootBlockId,
        usedSlideIds,
        reservedContentfulIds
      );

      return [{ ...content, id, media }];
    }
  );

  if (slides.length === 0) {
    logger.warn(
      "SectionHeroAdapter: slider has no compatible media; rendering a static hero instead",
      { blockId: rootBlockId }
    );
    return staticRootHero;
  }

  return {
    sys: item.sys,
    id: rootBlockId,
    __typename: "HeroSectionSlider",
    internalName: item.internalName,
    slides,
    autoplay: item.autoplay ?? false,
    autoplayDelay: normalizeAutoplayDelay(item.autoplayDuration),
  };
}
